import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid,
} from "recharts";
import { Users, Star, Flame, TrendingUp, Calendar, XCircle } from "lucide-react";

const COLORS = ["#1a2d56", "#d7de3b", "#3b82f6", "#10b981", "#f59e0b"];

export default function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <AdminLayout><div className="text-[#9aa6c0]">Chargement…</div></AdminLayout>;

  const profileData = Object.entries(s.by_profile).map(([name, value]) => ({ name, value }));
  const sourceData = Object.entries(s.by_source).map(([name, value]) => ({ name, value }));
  const monthlyData = Object.entries(s.monthly).map(([name, value]) => ({ name, value }));

  return (
    <AdminLayout>
      <h1 className="font-head text-2xl font-bold text-[#1a2d56] mb-1">Statistiques</h1>
      <p className="text-[#4b5b7c] text-sm mb-6">Performance de votre moteur de leads Restart.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Users} label="Total leads" value={s.total} />
        <Kpi icon={TrendingUp} label="Score moyen" value={`${s.avg_score}/100`} />
        <Kpi icon={Flame} label="Leads chauds" value={s.hot} />
        <Kpi icon={Star} label="Prioritaires" value={s.prioritaire} />
        <Kpi icon={Calendar} label="Taux de RDV" value={`${s.rdv_rate}%`} />
        <Kpi icon={TrendingUp} label="Taux d’inscription" value={`${s.inscrit_rate}%`} accent />
        <Kpi icon={XCircle} label="Taux de perte" value={`${s.perdu_rate}%`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Leads par profil">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={profileData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {profileData.map((e) => <Cell key={e.name} fill={COLORS[profileData.indexOf(e) % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <Legend data={profileData} />
        </Card>

        <Card title="Leads par source">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={sourceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#4b5b7c" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#4b5b7c" }} />
              <Tooltip />
              <Bar dataKey="value" fill="#1a2d56" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Évolution mensuelle">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#4b5b7c" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#4b5b7c" }} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#d7de3b" strokeWidth={3} dot={{ r: 4, fill: "#1a2d56" }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Conversion par profil">
          <div className="space-y-3 pt-2">
            {Object.entries(s.conv_profile).map(([name, v]) => {
              const rate = v.total ? Math.round((v.inscrit / v.total) * 100) : 0;
              return (
                <div key={name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#1a2d56] font-medium">{name}</span>
                    <span className="text-[#4b5b7c]">{v.inscrit}/{v.total} · {rate}%</span>
                  </div>
                  <div className="h-2.5 bg-[#f0f2f8] rounded-full overflow-hidden">
                    <div className="h-full bg-[#d7de3b] rounded-full" style={{ width: `${rate}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${accent ? "bg-[#d7de3b] text-[#1a2d56]" : "bg-[#f0f2f8] text-[#1a2d56]"}`}>
        <Icon size={18} />
      </div>
      <div className="font-head text-2xl font-extrabold text-[#1a2d56]">{value}</div>
      <div className="text-xs text-[#4b5b7c] mt-0.5">{label}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6">
      <h2 className="font-head font-bold text-[#1a2d56] mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Legend({ data }) {
  return (
    <div className="flex flex-wrap gap-3 mt-3 justify-center">
      {data.map((d, i) => (
        <span key={d.name} className="inline-flex items-center gap-1.5 text-xs text-[#4b5b7c]">
          <span className="w-3 h-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} /> {d.name}
        </span>
      ))}
    </div>
  );
}
